import { Card, CardContent } from "@/components/ui/card";
import { Flag, Trophy } from "lucide-react";

export function OyesTab({ round, PAR_3_HOLES }) {
  return (
    <Card className="card-default">
      <CardContent className="p-4">
        <h3 className="text-lg font-bold text-[#0F172A] mb-2 flex items-center gap-2">
          <Flag className="w-5 h-5 text-amber-600" />
          Resultados de Oyes
        </h3>
        <p className="text-xs text-[#64748B] mb-4">
          El oyes solo aplica en hoyos par 3 (hoyos {PAR_3_HOLES.join(', ')})
        </p>

        {/* Oyes Results by Hole */}
        <div className="space-y-4 mb-6">
          <h4 className="text-sm font-semibold text-[#64748B]">Por Hoyo</h4>
          {PAR_3_HOLES.map(holeNum => {
            const oyesResult = round?.oyes_results?.[String(holeNum)];
            return (
              <div key={holeNum} className="p-3 rounded-lg bg-amber-50 border border-amber-100">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-semibold text-[#0F172A]">Hoyo {holeNum}</span>
                  {oyesResult ? (
                    <span className="text-xs bg-amber-200 text-amber-800 px-2 py-0.5 rounded-full">Jugado</span>
                  ) : (
                    <span className="text-xs bg-gray-200 text-gray-600 px-2 py-0.5 rounded-full">Sin datos</span>
                  )}
                </div>
                {oyesResult && (
                  <div className="text-sm">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-amber-700 font-medium">1</span>
                      <span className={`w-2 h-2 rounded-full ${oyesResult.first_place?.is_base ? 'bg-[#1B4D3E]' : 'bg-[#D4A373]'}`}></span>
                      <span>{oyesResult.first_place?.player_name}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-amber-600 font-medium">2</span>
                      <span className={`w-2 h-2 rounded-full ${oyesResult.second_place?.is_base ? 'bg-[#1B4D3E]' : 'bg-[#D4A373]'}`}></span>
                      <span>{oyesResult.second_place?.player_name}</span>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Oyes Points Summary by Matchup */}
        <div className="space-y-3">
          <h4 className="text-sm font-semibold text-[#64748B]">Puntos por Enfrentamiento</h4>
          {round?.matchups?.map((matchup, index) => {
            const secPlayers = matchup.secondary_pair.map(id => 
              round.players.find(p => p.player_id === id)
            );
            const oyesPts = matchup.total_oyes_points || 0;
            
            return (
              <div 
                key={index}
                className="flex items-center justify-between p-3 rounded-lg bg-gray-50"
              >
                <span className="text-sm text-[#0F172A]">
                  Base vs {secPlayers.map(p => p?.name?.split(' ')[0]).join('+')}
                </span>
                <span className={`text-lg font-bold ${
                  oyesPts > 0 ? 'text-green-600' : 
                  oyesPts < 0 ? 'text-red-600' : 'text-gray-400'
                }`}>
                  {oyesPts > 0 ? '+' : ''}{oyesPts}
                </span>
              </div>
            );
          })}
        </div>

        {/* Grand Slam Section */}
        {round?.matchups?.some(m => m.grandslam?.active) && (
          <div className="space-y-3 mt-6">
            <h4 className="text-sm font-semibold text-[#64748B] flex items-center gap-2">
              <Trophy className="w-4 h-4 text-yellow-500" />
              Grand Slam
            </h4>
            <div className="p-4 rounded-lg bg-gradient-to-r from-yellow-50 to-amber-50 border border-yellow-200">
              <p className="text-xs text-amber-700 mb-3">
                Una pareja gana todos los oyes del dia. Bonus: {round?.matchups?.some(m => m.is_doubled) ? '6 pts (doble) / ' : ''}4 pts (normal).
              </p>
              {round?.matchups?.map((matchup, index) => {
                const gs = matchup.grandslam;
                if (!gs?.active) return null;
                const secPlayers = matchup.secondary_pair.map(id => 
                  round.players.find(p => p.player_id === id)
                );
                const winnerTeam = gs.team === 'base' ? 'Base' : secPlayers.map(p => p?.name?.split(' ')[0]).join('+');
                return (
                  <div 
                    key={index}
                    className="flex items-center justify-between p-3 rounded-lg bg-white/80 mb-2 last:mb-0"
                    data-testid={`grandslam-matchup-${index}`}
                  >
                    <div className="text-sm">
                      <span className="font-bold text-amber-800">E{index + 1}:</span>
                      <span className="ml-1 text-[#0F172A]">Base vs {secPlayers.map(p => p?.name?.split(' ')[0]).join('+')}</span>
                      <span className="ml-2 text-xs bg-yellow-200 text-yellow-800 px-2 py-0.5 rounded-full font-semibold">
                        {winnerTeam} GRAND SLAM
                      </span>
                    </div>
                    <span className={`text-lg font-bold ${gs.bonus > 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {gs.bonus > 0 ? '+' : ''}{gs.bonus}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Oyes Legend */}
        <div className="mt-4 pt-4 border-t border-gray-200">
          <p className="text-xs text-[#64748B]">
            <strong>Reglas del Oyes:</strong> 1 y 2 lugar por cercania a bandera. 
            Si el 1 es base - Base gana en los 3 enfrentamientos. 
            Si el 1 y 2 son secundarios - Secundarios ganan en los 3. 
            Si el 1 es secundario y 2 es base - Secundario gana solo en sus 2 parejas.
          </p>
        </div>
      </CardContent>
    </Card>
  );
}
